import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Megaphone, Send, Archive, CalendarRange, Globe, Users, X, EyeOff, Mail, ChevronDown, ChevronRight, RotateCw } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate, formatDateOnly } from "@/lib/cn";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/Badge";
import { Modal } from "@/components/Modal";
import { AttachmentField } from "@/components/AttachmentField";
import { AttachmentGallery } from "@/components/AttachmentThumb";
import { RulePanel } from "@/components/RulePanel";
import { QueryError } from "@/components/QueryError";
import { AdminRecordActions } from "@/components/AdminRecordActions";

type Status = "DRAFT" | "SENT" | "PUBLISHED" | "ARCHIVED";
type Category = "GENERAL" | "MAINTENANCE" | "SECURITY" | "FINANCIAL" | "REGULATORY" | "URGENT";

interface Announcement {
  id: string;
  title: string;
  content: string;
  category: Category;
  status: Status;
  publishedAt?: string | null;
  /** Validity window, calendar dates (midnight UTC), both inclusive; either may be open. */
  startsAt?: string | null;
  expiresAt?: string | null;
  attachmentUrls: string[];
  publishedBy?: { firstName: string; lastName: string } | null;
  createdAt: string;
  /** Administration only: emails sent so far (one per address per send) and the last one. */
  deliveryCount?: number;
  lastSentAt?: string | null;
  /** Who sees it in the portal once published: every unit, or the units it was emailed to. */
  audienceAll?: boolean;
  audienceUnits?: string[];
  /** Also on the public landing page, to anyone without signing in. */
  showOnPublicPage?: boolean;
}

const statusVariant: Record<Status, "default" | "warning" | "info" | "success"> = {
  DRAFT: "warning",
  SENT: "info",
  PUBLISHED: "success",
  ARCHIVED: "default",
};

const categoryVariant: Record<Category, "default" | "info" | "warning" | "danger" | "success" | "purple"> = {
  GENERAL: "info",
  MAINTENANCE: "warning",
  SECURITY: "danger",
  FINANCIAL: "success",
  REGULATORY: "purple",
  URGENT: "danger",
};

// Photos and documents, from a computer or straight from a phone (camera, gallery, files).
const ATTACH_ACCEPT = ".pdf,.doc,.docx,.jpg,.jpeg,.png,.heic,.heif,.webp,image/*";

/** Where an announcement stands against its validity window today (local calendar date). */
function windowState(a: Announcement): "scheduled" | "expired" | null {
  const today = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD
  if (a.startsAt && a.startsAt.slice(0, 10) > today) return "scheduled";
  if (a.expiresAt && a.expiresAt.slice(0, 10) < today) return "expired";
  return null;
}

export function AnnouncementsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState<Announcement | null>(null);
  const [publishFor, setPublishFor] = useState<Announcement | null>(null);

  const { data: items = [], isLoading, isError } = useQuery({
    queryKey: ["announcements"],
    queryFn: async () => (await api.get<Announcement[]>("/announcements")).data,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/announcements/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["announcements"] }),
  });

  const archiveMutation = useMutation({
    mutationFn: async (id: string) => (await api.post(`/announcements/${id}/archive`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["announcements"] }),
  });

  const canManage = user?.role === "ADMIN" || user?.role === "BOARD_MEMBER";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Megaphone size={24} /> {t("announcements.title")}
        </h1>
        {canManage && (
          <button onClick={() => setOpen(true)} className="btn-primary">
            <Plus size={16} /> {t("announcements.create")}
          </button>
        )}
      </div>

      {isError ? (
        <QueryError />
      ) : isLoading ? (
        <div className="text-slate-500">{t("common.loading")}</div>
      ) : items.length === 0 ? (
        <div className="card text-slate-500">—</div>
      ) : (
        <div className="space-y-4">
          {items.map((a) => {
            const state = windowState(a);
            const from = a.startsAt ? formatDateOnly(a.startsAt, i18n.language) : null;
            const to = a.expiresAt ? formatDateOnly(a.expiresAt, i18n.language) : null;
            return (
            <div key={a.id} className="card">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <Badge variant={categoryVariant[a.category]}>{t(`announcements.cat.${a.category}`)}</Badge>
                    {canManage && <Badge variant={statusVariant[a.status]}>{t(`announcements.${a.status.toLowerCase()}`)}</Badge>}
                    {canManage && state && (
                      <Badge variant={state === "scheduled" ? "info" : "default"}>{t(`announcements.${state}`)}</Badge>
                    )}
                    {canManage && a.showOnPublicPage && (
                      <Badge variant="purple">
                        <Globe size={11} className="mr-1" /> {t("announcements.publicBadge")}
                      </Badge>
                    )}
                    {a.publishedAt && <span className="text-xs text-slate-500">· {formatDate(a.publishedAt, i18n.language)}</span>}
                    {canManage && a.status === "SENT" && a.lastSentAt && (
                      <span className="text-xs text-slate-500">· {formatDate(a.lastSentAt, i18n.language)}</span>
                    )}
                  </div>
                  {(from || to) && (
                    <div className="text-xs text-slate-500 mb-1 inline-flex items-center gap-1">
                      <CalendarRange size={12} />
                      {from && to
                        ? t("announcements.validRange", { from, to })
                        : from
                          ? t("announcements.validFromOnly", { from })
                          : t("announcements.validUntilOnly", { to })}
                    </div>
                  )}
                  <h3 className="text-lg font-semibold">{a.title}</h3>
                  <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{a.content}</div>
                  {a.attachmentUrls.length > 0 && (
                    <div className="mt-3 text-sm space-y-2">
                      <strong>{t("announcements.attachments")}:</strong>
                      <AttachmentGallery urls={a.attachmentUrls} />
                    </div>
                  )}
                  {a.publishedBy && <div className="text-xs text-slate-500 mt-2">{t("announcements.publishedBy")}: {a.publishedBy.firstName} {a.publishedBy.lastName}</div>}
                  {canManage && a.status === "PUBLISHED" && (
                    <div className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500">
                      <Users size={12} />
                      {a.audienceAll
                        ? t("announcements.audienceAll")
                        : a.audienceUnits?.length
                          ? t("announcements.audienceUnits", { units: a.audienceUnits.join(", ") })
                          : t("announcements.audienceNone")}
                    </div>
                  )}
                  {canManage && <DeliveriesSection announcement={a} />}
                </div>
                {canManage && (
                  <div className="flex flex-col gap-2 shrink-0">
                    {(a.status === "DRAFT" || a.status === "SENT") && (
                      <button className="btn-primary text-xs" onClick={() => setPublishFor(a)}>
                        <Send size={12} /> {t("announcements.publish")}
                      </button>
                    )}
                    {a.status === "PUBLISHED" && (
                      <button className="btn-secondary text-xs" onClick={() => archiveMutation.mutate(a.id)}>
                        <Archive size={12} /> {t("announcements.archive")}
                      </button>
                    )}
                    <AdminRecordActions
                      onEdit={() => setEditItem(a)}
                      onDelete={() => deleteMutation.mutate(a.id)}
                      entityLabel="el comunicado"
                    />
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {publishFor && (
        <PublishModal
          announcement={publishFor}
          onClose={() => setPublishFor(null)}
          onDone={() => {
            qc.invalidateQueries({ queryKey: ["announcements"] });
            qc.invalidateQueries({ queryKey: ["announcement-deliveries", publishFor.id] });
          }}
        />
      )}
      <CreateModal open={open} onClose={() => setOpen(false)} onDone={() => qc.invalidateQueries({ queryKey: ["announcements"] })} />
      {editItem && (
        <CreateModal
          key={editItem.id}
          open
          announcement={editItem}
          onClose={() => setEditItem(null)}
          onDone={() => qc.invalidateQueries({ queryKey: ["announcements"] })}
        />
      )}
    </div>
  );
}

function CreateModal({ open, announcement, onClose, onDone }: { open: boolean; announcement?: Announcement | null; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const isEdit = !!announcement;
  const [form, setForm] = useState({
    title: announcement?.title ?? "",
    content: announcement?.content ?? "",
    category: (announcement?.category ?? "GENERAL") as Category,
    startsAt: announcement?.startsAt ? announcement.startsAt.slice(0, 10) : "",
    expiresAt: announcement?.expiresAt ? announcement.expiresAt.slice(0, 10) : "",
    showOnPublicPage: announcement?.showOnPublicPage ?? false,
  });
  const rangeInvalid = !!form.startsAt && !!form.expiresAt && form.expiresAt < form.startsAt;
  const [attachmentUrls, setAttachmentUrls] = useState<string[]>(announcement?.attachmentUrls ?? []);

  const mutation = useMutation({
    mutationFn: async () => {
      const body = {
        ...form,
        // Calendar dates travel as midnight UTC, like every date-only field.
        startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
        expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
        attachmentUrls: attachmentUrls.filter(Boolean),
      };
      if (isEdit && announcement) return (await api.put(`/announcements/${announcement.id}`, body)).data;
      return (await api.post("/announcements", body)).data;
    },
    onSuccess: () => {
      onDone();
      onClose();
      if (!isEdit) {
        setForm({ title: "", content: "", category: "GENERAL", startsAt: "", expiresAt: "", showOnPublicPage: false });
        setAttachmentUrls([]);
      }
    },
  });

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? t("common.edit") : t("announcements.create")} size="lg">
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); if (!rangeInvalid) mutation.mutate(); }}>
        <div>
          <label className="label">{t("common.title")}</label>
          <input className="input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div>
          <label className="label">{t("common.category")}</label>
          <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as Category })}>
            {(["GENERAL", "MAINTENANCE", "SECURITY", "FINANCIAL", "REGULATORY", "URGENT"] as Category[]).map((c) => (
              <option key={c} value={c}>{t(`announcements.cat.${c}`)}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">{t("announcements.validFrom")} <span className="text-slate-400 font-normal">({t("common.optional")})</span></label>
              <input type="date" className="input" value={form.startsAt} onChange={(e) => setForm({ ...form, startsAt: e.target.value })} />
            </div>
            <div>
              <label className="label">{t("announcements.validUntil")} <span className="text-slate-400 font-normal">({t("common.optional")})</span></label>
              <input
                type="date"
                className="input"
                min={form.startsAt || undefined}
                value={form.expiresAt}
                onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
              />
            </div>
          </div>
          <p className={`text-xs mt-1 ${rangeInvalid ? "text-red-600" : "text-slate-500"}`}>
            {rangeInvalid ? t("announcements.rangeInvalid") : t("announcements.validityHint")}
          </p>
        </div>
        <div>
          <label className="label">{t("announcements.content")}</label>
          <textarea className="input min-h-[160px]" required value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
        </div>
        {form.category === "REGULATORY" && (
          <RulePanel tags={["fine", "access", "pet", "parking"]} />
        )}
        <AttachmentField
          label={t("announcements.attachments")}
          folder="costadorada1/announcements"
          accept={ATTACH_ACCEPT}
          addLabel={t("announcements.addPhotoOrDoc")}
          urls={attachmentUrls}
          onChange={setAttachmentUrls}
          allowLink
          linkPlaceholder={t("announcements.linkPlaceholder")}
        />
        <label className="flex items-start gap-2 rounded border border-slate-200 px-3 py-2 cursor-pointer hover:bg-slate-50">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={form.showOnPublicPage}
            onChange={(e) => setForm({ ...form, showOnPublicPage: e.target.checked })}
          />
          <span>
            <span className="text-sm font-medium text-slate-800">{t("announcements.showOnPublicPage")}</span>
            <span className="block text-xs text-slate-500">{t("announcements.showOnPublicPageHint")}</span>
          </span>
        </label>
        <p className="text-xs text-slate-500">{t("announcements.draftNote")}</p>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn-primary" disabled={mutation.isPending || rangeInvalid}>{t("common.save")}</button>
        </div>
        {mutation.isError && (
          <div className="text-sm text-red-600">{(mutation.error as any)?.response?.data?.message ?? t("common.error")}</div>
        )}
      </form>
    </Modal>
  );
}

interface Recipient {
  id: string;
  unitNumber: string;
  building: string | null;
  ownerName: string;
  ownerEmail: string;
  ownerEmail2?: string | null;
  /** The tenant living in the unit now, if it is rented. */
  tenant?: { name: string; email?: string | null; email2?: string | null } | null;
}
interface BoardRecipient {
  name: string;
  position: string;
  email: string;
}
interface Delivery {
  recipients: number;
  sent: number;
  failed: string[];
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const byUnit = (a: Recipient, b: Recipient) => a.unitNumber.localeCompare(b.unitNumber, undefined, { numeric: true });

/**
 * Who gets the email, chosen before anything is sent: every unit owner is ticked to start
 * with; the list filters by building or by name, owners can be ticked off, the Junta
 * Directiva added with one tick, and extra addresses typed in. Everyone receives it in BCC,
 * so nobody sees the others' addresses. Publishing in the portal is a separate tick, off by
 * default: without it the email goes out and the announcement is marked "Enviado", still
 * hidden from owners (e.g. a draft sent to the Junta for review).
 */
function PublishModal({ announcement, onClose, onDone }: { announcement: Announcement; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["announcement-recipients"],
    queryFn: async () => (await api.get<{ owners: Recipient[]; board: BoardRecipient[] }>("/announcements/recipients")).data,
  });
  const recipients = useMemo(() => data?.owners ?? [], [data]);
  const board = data?.board ?? [];
  const [includeBoard, setIncludeBoard] = useState(false);
  const [includeTenants, setIncludeTenants] = useState(true);
  const [publish, setPublish] = useState(false);
  const sorted = useMemo(() => [...recipients].sort(byUnit), [recipients]);
  const [selected, setSelected] = useState<Set<string> | null>(null);
  useEffect(() => {
    if (selected === null && recipients.length) setSelected(new Set(recipients.map((r) => r.id)));
  }, [recipients, selected]);
  const chosen = selected ?? new Set<string>();

  const [building, setBuilding] = useState("");
  const [search, setSearch] = useState("");
  const buildings = [...new Set(recipients.map((r) => r.building).filter((b): b is string => !!b))].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true })
  );
  const needle = search.trim().toLowerCase();
  const visible = sorted.filter(
    (r) =>
      (!building || r.building === building) &&
      (!needle || [r.unitNumber, r.ownerName, r.ownerEmail].join(" ").toLowerCase().includes(needle))
  );
  const setVisible = (on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev ?? []);
      for (const r of visible) on ? next.add(r.id) : next.delete(r.id);
      return next;
    });
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev ?? []);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const [extra, setExtra] = useState<string[]>([]);
  const [extraInput, setExtraInput] = useState("");
  const [extraError, setExtraError] = useState<string | null>(null);
  function addExtra() {
    const parts = extraInput.split(/[\s,;]+/).map((p) => p.trim().toLowerCase()).filter(Boolean);
    const bad = parts.filter((p) => !EMAIL_RE.test(p));
    const good = parts.filter((p) => EMAIL_RE.test(p) && !extra.includes(p));
    if (good.length) setExtra([...extra, ...good]);
    setExtraError(bad.length ? t("announcements.invalidEmails", { list: bad.join(", ") }) : null);
    setExtraInput(bad.join(" "));
  }

  // What will actually go out, each address once: the chosen units' owners (both addresses),
  // their tenants unless left out, the Junta when ticked, and extras.
  const addr = (e?: string | null) => (e ? [e.trim().toLowerCase()] : []);
  const chosenUnits = recipients.filter((r) => chosen.has(r.id));
  const rentedCount = recipients.filter((r) => r.tenant).length;
  const total = new Set([
    ...chosenUnits.flatMap((r) => [...addr(r.ownerEmail), ...addr(r.ownerEmail2)]),
    ...(includeTenants ? chosenUnits.flatMap((r) => (r.tenant ? [...addr(r.tenant.email), ...addr(r.tenant.email2)] : [])) : []),
    ...(includeBoard ? board.map((b) => b.email.trim().toLowerCase()) : []),
    ...extra,
  ]).size;
  const canGo = publish || total > 0;

  const [delivery, setDelivery] = useState<(Delivery & { published: boolean }) | null>(null);
  const mutation = useMutation({
    mutationFn: async () =>
      (await api.post<Announcement & { delivery: Delivery }>(`/announcements/${announcement.id}/send`, {
        unitIds: [...chosen],
        extraEmails: extra,
        includeBoard,
        includeTenants,
        publish,
      })).data,
    onSuccess: (res) => {
      setDelivery({ ...res.delivery, published: res.status === "PUBLISHED" });
      onDone();
    },
  });

  return (
    <Modal open onClose={onClose} title={`${t("announcements.publishTitle")} — ${announcement.title}`} size="lg">
      {delivery ? (
        <div className="space-y-3 text-sm">
          <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-green-800">
            {delivery.recipients === 0
              ? t("announcements.publishedNoEmail")
              : delivery.published
                ? t("announcements.deliveryDone", { sent: delivery.sent, recipients: delivery.recipients })
                : t("announcements.sentDraft", { sent: delivery.sent, recipients: delivery.recipients })}
          </div>
          {delivery.failed.length > 0 && (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
              {t("announcements.deliveryFailed", { count: delivery.failed.length, list: delivery.failed.join(", ") })}
            </div>
          )}
          <div className="flex justify-end">
            <button className="btn-primary" onClick={onClose}>{t("common.close")}</button>
          </div>
        </div>
      ) : (
        <div className="space-y-4 text-sm">
          <div>
            <div className="label">{t("announcements.recipientsOwners")}</div>
            <div className="flex flex-wrap gap-2">
              <select className="input max-w-[11rem]" value={building} onChange={(e) => setBuilding(e.target.value)} aria-label={t("hr.building")}>
                <option value="">{t("announcements.allBuildings")}</option>
                {buildings.map((b) => (
                  <option key={b} value={b}>{t("announcements.buildingN", { b })}</option>
                ))}
              </select>
              <input
                type="search"
                className="input flex-1 min-w-[12rem]"
                placeholder={t("announcements.searchRecipients")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs">
              <span className="text-slate-600">
                {t("announcements.selectedCount", { selected: chosen.size, total: recipients.length })}
              </span>
              <span className="flex gap-3">
                <button type="button" className="text-primary hover:underline" onClick={() => setVisible(true)}>
                  {t("announcements.selectVisible")}
                </button>
                <button type="button" className="text-primary hover:underline" onClick={() => setVisible(false)}>
                  {t("announcements.clearVisible")}
                </button>
              </span>
            </div>
            <div className="mt-2 max-h-64 overflow-y-auto rounded border border-slate-200 divide-y divide-slate-100">
              {isError ? (
                <div className="p-3 text-red-600">{t("common.error")}</div>
              ) : isLoading ? (
                <div className="p-3 text-slate-500">{t("common.loading")}</div>
              ) : visible.length === 0 ? (
                <div className="p-3 text-slate-500">{t("announcements.noRecipients")}</div>
              ) : (
                visible.map((r) => (
                  <label key={r.id} className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-slate-50">
                    <input type="checkbox" checked={chosen.has(r.id)} onChange={() => toggle(r.id)} />
                    <span className="w-12 shrink-0 font-medium text-primary-800">{r.unitNumber}</span>
                    <span className="min-w-0 flex-1 text-slate-700">
                      <span className="block truncate">{r.ownerName}</span>
                      {r.tenant && (
                        <span className="block truncate text-xs text-amber-800">+ {t("announcements.tenantLabel")}: {r.tenant.name}</span>
                      )}
                    </span>
                    <span className="hidden sm:block max-w-[14rem] text-xs text-slate-500">
                      <span className="block truncate">{r.ownerEmail}</span>
                      {r.ownerEmail2 && <span className="block truncate">{r.ownerEmail2}</span>}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>

          <label className="flex items-start gap-2 rounded border border-slate-200 px-3 py-2 cursor-pointer hover:bg-slate-50">
            <input type="checkbox" className="mt-0.5" checked={includeTenants} onChange={(e) => setIncludeTenants(e.target.checked)} />
            <span>
              <span className="font-medium text-slate-800">{t("announcements.includeTenants", { count: rentedCount })}</span>
              <span className="block text-xs text-slate-500">{t("announcements.includeTenantsHint")}</span>
            </span>
          </label>

          <label className="flex items-start gap-2 rounded border border-slate-200 px-3 py-2 cursor-pointer hover:bg-slate-50">
            <input type="checkbox" className="mt-0.5" checked={includeBoard} onChange={(e) => setIncludeBoard(e.target.checked)} />
            <span>
              <span className="font-medium text-slate-800">{t("announcements.includeBoard", { count: board.length })}</span>
              {board.length > 0 && <span className="block text-xs text-slate-500">{board.map((b) => b.name).join(", ")}</span>}
            </span>
          </label>

          <div>
            <label className="label">{t("announcements.extraEmails")}</label>
            <div className="flex gap-2">
              <input
                className="input"
                inputMode="email"
                placeholder={t("announcements.extraPlaceholder")}
                value={extraInput}
                onChange={(e) => setExtraInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addExtra();
                  }
                }}
              />
              <button type="button" className="btn-secondary shrink-0" onClick={addExtra} disabled={!extraInput.trim()}>
                <Plus size={14} /> {t("announcements.addEmail")}
              </button>
            </div>
            {extraError && <p className="mt-1 text-xs text-red-600">{extraError}</p>}
            {extra.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {extra.map((e) => (
                  <span key={e} className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs">
                    {e}
                    <button type="button" className="text-slate-400 hover:text-red-600" onClick={() => setExtra(extra.filter((x) => x !== e))} aria-label={t("common.delete")}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <label className="flex items-start gap-2 rounded border border-slate-200 px-3 py-2 cursor-pointer hover:bg-slate-50">
            <input type="checkbox" className="mt-0.5" checked={publish} onChange={(e) => setPublish(e.target.checked)} />
            <span>
              <span className="font-medium text-slate-800">{t("announcements.publishInPortal")}</span>
              <span className="block text-xs text-slate-500">
                {publish ? t("announcements.publishOnHint") : t("announcements.publishOffHint")}
              </span>
            </span>
          </label>

          <p className="flex items-start gap-1.5 text-xs text-slate-500">
            <EyeOff size={13} className="mt-0.5 shrink-0" /> {t("announcements.bccNote")}
          </p>
          {mutation.isError && (
            <div className="text-sm text-red-600">{(mutation.error as any)?.response?.data?.message ?? t("common.error")}</div>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
            <button type="button" className="btn-primary" disabled={mutation.isPending || isLoading || !canGo} onClick={() => mutation.mutate()}>
              <Send size={14} />{" "}
              {publish
                ? total > 0
                  ? t("announcements.sendTo", { count: total })
                  : t("announcements.publishNoEmail")
                : total > 0
                  ? t("announcements.sendOnlyTo", { count: total })
                  : t("announcements.chooseRecipients")}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

interface Delivery {
  id: string;
  kind: "OWNER" | "TENANT" | "BOARD" | "EXTRA";
  email: string;
  recipientName?: string | null;
  unitNumber?: string | null;
  status: "SENT" | "FAILED";
  error?: string | null;
  resend: boolean;
  sentAt: string;
  sentBy?: { firstName: string; lastName: string } | null;
}
interface LegacySend {
  sentAt: string;
  published: boolean;
  recipients: number | null;
  by: string | null;
}

/**
 * The evidence of each email sent for an announcement: who, at which address, when, by
 * whom, and whether the mail service took it. Searchable by unit, name or email, with a
 * re-send per recipient (a second notice, or an address that was refused).
 */
function DeliveriesSection({ announcement }: { announcement: Announcement }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["announcement-deliveries", announcement.id],
    queryFn: async () =>
      (await api.get<{ deliveries: Delivery[]; legacy: LegacySend[] }>(`/announcements/${announcement.id}/deliveries`)).data,
    enabled: open,
  });
  const resend = useMutation({
    mutationFn: async (d: Delivery) => (await api.post(`/announcements/${announcement.id}/resend`, { deliveryId: d.id })).data,
    onError: (e: any) => alert(e?.response?.data?.message ?? t("common.error")),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["announcement-deliveries", announcement.id] });
      qc.invalidateQueries({ queryKey: ["announcements"] });
    },
  });

  const who = (d: Delivery) =>
    d.kind === "OWNER"
      ? [d.unitNumber, d.recipientName].filter(Boolean).join(" – ")
      : d.kind === "TENANT"
        ? `${d.unitNumber ?? ""} – ${t("announcements.tenantLabel")}: ${d.recipientName ?? ""}`
        : d.kind === "BOARD"
        ? `${t("announcements.deliveryBoard")}: ${d.recipientName ?? ""}`
        : t("announcements.deliveryExtra");
  const needle = search.trim().toLowerCase();
  const rows = (data?.deliveries ?? []).filter(
    (d) => !needle || [d.unitNumber, d.recipientName, d.email].filter(Boolean).join(" ").toLowerCase().includes(needle)
  );
  const count = announcement.deliveryCount ?? 0;

  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      <button
        type="button"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-primary"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Mail size={14} /> {t("announcements.deliveriesTitle", { count })}
        {announcement.lastSentAt && (
          <span className="font-normal text-slate-500">
            · {t("announcements.lastSent", { date: formatDate(announcement.lastSentAt, i18n.language) })}
          </span>
        )}
      </button>
      {open && (
        <div className="mt-2 space-y-2 text-sm">
          {isError ? (
            <div className="text-red-600">{t("common.error")}</div>
          ) : isLoading || !data ? (
            <div className="text-slate-500">{t("common.loading")}</div>
          ) : (
            <>
              {data.deliveries.length > 0 && (
                <input
                  type="search"
                  className="input max-w-xs"
                  placeholder={t("announcements.deliveriesSearch")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              )}
              {data.deliveries.length === 0 && data.legacy.length === 0 ? (
                <p className="text-slate-500">{t("announcements.deliveriesNone")}</p>
              ) : (
                <div className="max-h-80 overflow-auto rounded border border-slate-200">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">{t("announcements.deliverySentAt")}</th>
                        <th className="px-3 py-2 text-left">{t("announcements.deliveryRecipient")}</th>
                        <th className="px-3 py-2 text-left">{t("announcements.deliveryEmail")}</th>
                        <th className="px-3 py-2 text-left">{t("announcements.deliveryStatus")}</th>
                        <th className="px-3 py-2 text-left">{t("announcements.deliverySentBy")}</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {rows.map((d) => (
                        <tr key={d.id}>
                          <td className="whitespace-nowrap px-3 py-1.5">{formatDate(d.sentAt, i18n.language)}</td>
                          <td className="px-3 py-1.5">
                            {who(d)}
                            {d.resend && (
                              <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">{t("announcements.deliveryResendTag")}</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 break-all">{d.email}</td>
                          <td className="px-3 py-1.5" title={d.error ?? undefined}>
                            <Badge variant={d.status === "SENT" ? "success" : "danger"}>
                              {d.status === "SENT" ? t("announcements.deliverySent") : t("announcements.deliveryStatusFailed")}
                            </Badge>
                          </td>
                          <td className="px-3 py-1.5">{d.sentBy ? `${d.sentBy.firstName} ${d.sentBy.lastName}` : "—"}</td>
                          <td className="px-3 py-1.5 text-right">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 text-primary hover:underline disabled:opacity-50"
                              disabled={resend.isPending}
                              onClick={() => {
                                if (window.confirm(t("announcements.resendConfirm", { email: d.email }))) resend.mutate(d);
                              }}
                            >
                              <RotateCw size={11} /> {t("announcements.resend")}
                            </button>
                          </td>
                        </tr>
                      ))}
                      {data.legacy.map((l, i) => (
                        <tr key={`legacy-${i}`} className="text-slate-500">
                          <td className="whitespace-nowrap px-3 py-1.5">{formatDate(l.sentAt, i18n.language)}</td>
                          <td className="px-3 py-1.5" colSpan={5}>
                            {t("announcements.deliveryLegacy", { count: l.recipients ?? 0 })}
                            {l.by ? ` · ${l.by}` : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
